import type { ReactNode } from 'react';
import NumberRenderer from '@/app/common/components/CellRenderers/NumberRenderer';

type ColumnInput = {
  type?: string;
  field?: string;
  title?: string;
  tooltip?: string | null;
  icon?: string;
  gp?: string;
  gptooltip?: string | null;
  gpicon?: string;
  searchable?: boolean;
  noFilter?: boolean;
  suppressRowTransform?: boolean;
  cellRenderer?: unknown;
  [key: string]: unknown;
};

type ColumnsSettings = {
  columnsState?: unknown;
};

type GroupedColumn = {
  headerGroupComponentParams: {
    gptooltip: string | null;
    gpicon: string;
  };
  headerName: string | undefined;
  children: Record<string, unknown>[];
};

export const createColumnsDef = (
  columns: ColumnInput[],
  settings?: ColumnsSettings
): GroupedColumn[] | Record<string, unknown>[] | undefined => {
  const modifiedColumns = columns.map(item => {
    if (item.type === 'number') {
      item.cellRenderer =
        item.cellRenderer ||
        ((params: { data: Record<string, unknown> }) => (
          <NumberRenderer text={params.data[item.field as string] as ReactNode} />
        ));
    }
    return item;
  });

  if (modifiedColumns.some(col => col.gp)) {
    const groupedColumns: Record<string, GroupedColumn> = {};
    modifiedColumns.forEach(col => {
      const groupKey = col.gp || col.field || '';
      if (!groupedColumns[groupKey]) {
        groupedColumns[groupKey] = {
          headerGroupComponentParams: {
            gptooltip: col.gptooltip || null,
            gpicon: col.gpicon || ''
          },
          headerName: col.gp,
          children: []
        };
      }

      const newColumn: Record<string, unknown> = {
        headerName: col.title,
        field: col.field,
        headerComponentParams: {
          tooltip: col.tooltip || null,
          icon: col.icon || ''
        },
        ...col
      };

      delete newColumn.title;
      delete newColumn.tooltip;
      delete newColumn.icon;
      delete newColumn.gp;
      delete newColumn.gptooltip;
      delete newColumn.gpicon;
      delete newColumn.searchable;
      delete newColumn.type;
      delete newColumn.noFilter;
      delete newColumn.suppressRowTransform;

      groupedColumns[groupKey].children.push(newColumn);
    });
    return Object.values(groupedColumns);
  }

  const newColumns = modifiedColumns.map(col => {
    const newColumn: Record<string, unknown> = {
      headerName: col.title,
      field: col.field,
      headerComponentParams: {
        tooltip: col.tooltip || null,
        icon: col.icon || ''
      },
      ...col
    };

    delete newColumn.title;
    delete newColumn.tooltip;
    delete newColumn.icon;
    delete newColumn.searchable;
    delete newColumn.type;
    delete newColumn.noFilter;
    delete newColumn.suppressRowTransform;

    return newColumn;
  });

  if (settings?.columnsState) return;
  return newColumns;
};
